import { resolveStorefrontFeatures, type StorefrontFeatureKey } from "@loot-os/api-contract";
import { skipToken, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";

import { labelList } from "./platformSwitches";
import { useTRPC } from "./trpc";
import { useStandingNotice } from "./useStandingNotice";
import { useTenantRole } from "./useTenantRole";

/**
 * The switches a tenant only cares about when the matching storefront feature is on. Warning a
 * tenant that battles are disabled when they never enabled battles is noise that reads like a
 * fault in their own setup. The rest (`opens`, `claims`, `orders`) have no feature to check:
 * every storefront does them.
 */
const SWITCH_REQUIRES_FEATURE: Record<string, StorefrontFeatureKey> = {
  battles: "battles",
  topups: "balancePurchases",
  chat: "chat",
  forge: "forge",
  live_breaks: "liveBreaks",
};

/**
 * Raises the standing notice when a platform switch the reader cares about is disabled, and
 * withdraws it the moment the last one comes back. Called from the two overview pages a reader
 * lands on: the platform admin's tenant table and a tenant's own overview.
 *
 * A toast rather than a banner, per `useStandingNotice` — but sticky, because this is a
 * condition the reader cannot fix by acting on the page and must keep in mind while they work.
 * Platform admins get a button to the switches page; a tenant gets none, because there is
 * nothing for them to do, so the wording says whose problem it is and that it is temporary.
 *
 * `me.platformStatus` is `authedProcedure` and filtered to the switches a tenant can observe, so
 * one read serves both readers. Inside a tenant it is filtered again, here, against that
 * environment's enabled features. Polled, because a switch flips outside this tab — during an
 * incident, by someone else.
 */
export function usePlatformSwitchNotice(): void {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { isPlatformAdmin } = useTenantRole();
  const { tenantId } = useParams({ strict: false }) as { tenantId?: string };

  const status = useQuery(
    trpc.me.platformStatus.queryOptions(undefined, {
      // The gates cache for SWITCH_CACHE_TTL_MS (5s); a minute is soon enough for a human
      // reading a page, and cheap — it is one primary-key scan of at most seven rows.
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
    }),
  );

  // The same query key `useCurrency` already uses on a tenant page, so this costs nothing there
  // and is skipped entirely outside a tenant.
  const storefront = useQuery(trpc.storefront.get.queryOptions(tenantId ? undefined : skipToken));

  const features = storefront.data?.config
    ? resolveStorefrontFeatures(storefront.data.config.features)
    : undefined;

  const disabled = (status.data?.disabled ?? []).filter((key) => {
    // Outside a tenant (the platform table) every switch is relevant: it is the platform's view.
    if (!tenantId) return true;
    const required = SWITCH_REQUIRES_FEATURE[key];
    if (!required) return true;
    // Until the config lands, say nothing rather than warn about a feature they may not run.
    return features?.[required] === true;
  });

  // Always "are": every tenant-visible label is a plural noun (Opens, Claims, Battles, Top-ups,
  // Withdrawals, Chat messages), so agreeing with the length of the list instead read "Opens is disabled".
  useStandingNotice(disabled.length > 0, {
    id: "platform-switches-disabled",
    message: isPlatformAdmin
      ? `${labelList(disabled)} are disabled platform-wide, for every tenant.`
      : `${labelList(disabled)} are temporarily unavailable across Loot OS. Nothing is wrong with your configuration — we are working on it.`,
    duration: Number.POSITIVE_INFINITY,
    ...(isPlatformAdmin
      ? { action: { label: "Review", onClick: () => navigate({ to: "/platform-switches" }) } }
      : {}),
  });
}
